
import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: React.ReactNode;
  aspectRatio?: number;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
}

export function LazyImage({ 
  src, 
  alt, 
  className, 
  fallback, 
  aspectRatio,
  loading = 'lazy',
  fetchPriority,
  ...props 
}: LazyImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(loading === 'eager'); // Eager load immediately
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!imgRef.current || loading === 'eager') return;
    
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, {
      rootMargin: "200px", // Start loading when within 200px of viewport
    });

    observer.observe(imgRef.current);

    return () => {
      observer.disconnect();
    };
  }, [loading]);

  const handleLoad = () => {
    setLoaded(true);
  };

  return (
    <div 
      className={cn(
        "relative overflow-hidden",
        aspectRatio && "w-full",
        className
      )}
      style={aspectRatio ? { paddingTop: `${(1 / aspectRatio) * 100}%` } : undefined}
      ref={imgRef}
    >
      {!loaded && (fallback || <Skeleton className="absolute inset-0" />)}
      {inView && (
        <img
          src={src}
          alt={alt || ""}
          className={cn(
            "transition-opacity duration-150 w-full h-full object-cover",
            aspectRatio && "absolute inset-0",
            loaded ? "opacity-100" : "opacity-0"
          )}
          loading={loading}
          decoding="async"
          fetchPriority={fetchPriority}
          onLoad={handleLoad}
          {...props}
        />
      )}
    </div>
  );
}
